// Browser transport for the cross-module Task cancellation intent. Kept separate so the success parser
// must prove both Tasks terminal truth and Agent cleanup counts before UI copy claims work was stopped.

import { API_PREFIX, safeNote } from './post-intent.ts';

/** A settled cancellation reports only the Agent work actually stopped; no provider undo is implied. */
export type TaskCancellationRouteResult =
  | {
      readonly ok: true;
      readonly interruptedRunCount: number;
      readonly cancelledQueuedTriggerCount: number;
    }
  | { readonly ok: false; readonly note?: string };

const taskCancelPath = (taskId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/cancel`;

export async function postTaskCancellation(
  taskId: string,
  expectedRevision: number,
  reason: string,
): Promise<TaskCancellationRouteResult> {
  const response = await fetch(taskCancelPath(taskId), {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ expectedRevision, reason }),
  });
  if (!response.ok) return { ok: false, note: await safeNote(response) };
  try {
    const payload = (await response.json()) as {
      readonly task?: { readonly state?: unknown; readonly closure?: { readonly kind?: unknown } };
      readonly agentCleanup?: {
        readonly interruptedRunCount?: unknown;
        readonly cancelledQueuedTriggerCount?: unknown;
      };
    };
    const interruptedRunCount = payload.agentCleanup?.interruptedRunCount;
    const cancelledQueuedTriggerCount = payload.agentCleanup?.cancelledQueuedTriggerCount;
    if (
      payload.task?.state === 'dropped' &&
      payload.task.closure?.kind === 'cancelled' &&
      isNonNegativeCount(interruptedRunCount) &&
      isNonNegativeCount(cancelledQueuedTriggerCount)
    ) {
      return { ok: true, interruptedRunCount, cancelledQueuedTriggerCount };
    }
  } catch {
    // A successful but unreadable settlement is not enough to claim the Agent cleanup completed.
  }
  return {
    ok: false,
    note: 'Ritona could not confirm that all active and queued work was stopped. Refresh before retrying.',
  };
}

function isNonNegativeCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}
